import React from 'react';
import SegmentedToggle from 'components/SegmentedToggle';
import InlineConditionPills from 'components/InlineConditionPills/InlineConditionPills';
import InlineFoilPills from 'components/InlineFoilPills/InlineFoilPills';
import FilterPill from 'components/FilterPill/FilterPill';
import {
  ENTRY_CONDITIONS,
  EntryCondition,
  HaveEntryDefaults,
  WantEntryDefaults,
} from 'constants/entryDefaults';
import { SUPPORTED_LANGUAGES } from 'constants/languages';
import { CardFinish, FINISH_LABELS, FINISH_ORDER } from 'utils/finishes';
import { EntryDefaultsPill, EntryDefaultsSection } from './EntryDefaultsPill';
import { EntryDefaultsApi } from './useEntryDefaults';

const CONDITION_OPTIONS = ENTRY_CONDITIONS.map((c) => ({ value: c, label: c }));
const FINISH_OPTIONS = FINISH_ORDER.map((f) => ({ value: f, label: FINISH_LABELS[f] }));
/**
 * One toggle chip per language, the same chip and order Settings uses, so any
 * language can be dropped in one click however many are selected. All twelve are
 * offered; Phyrexian is selectable but is not part of the stock defaults or
 * Reset. At least one stays on, and the selection is kept in display order so
 * the pill's summary leads with the same language Settings lists first.
 */
const WantLanguageChips: React.FC<{ value: string[]; onChange: (languages: string[]) => void }> = ({
  value,
  onChange,
}) => {
  const toggle = (code: string) => {
    const selected = value.includes(code) ? value.filter((c) => c !== code) : [...value, code];
    if (selected.length === 0) return;
    const inDisplayOrder = SUPPORTED_LANGUAGES.map((l) => l.code).filter((c) => selected.includes(c));
    // A stored language the editor does not offer is carried through untouched.
    onChange([...inDisplayOrder, ...selected.filter((c) => !inDisplayOrder.includes(c))]);
  };
  return (
    <div className="entry-defaults-language-chips" role="group" aria-label="Language">
      {SUPPORTED_LANGUAGES.map((l) => (
        <FilterPill key={l.code} label={l.displayName} active={value.includes(l.code)} onToggle={() => toggle(l.code)} />
      ))}
    </div>
  );
};

interface PillProps<T> {
  defaults: EntryDefaultsApi<T>;
  onDone?: () => void;
}

/** Have List: one condition, one finish, one language per physical copy. */
export const HaveEntryDefaultsPill: React.FC<PillProps<HaveEntryDefaults>> = ({ defaults, onDone }) => (
  <EntryDefaultsPill
    label="Adding as"
    summary={defaults.summary}
    isStock={defaults.isStock}
    title="New cards are added as"
    footer="Remembered for your Have List. Doesn't change cards already on it."
    onReset={defaults.reset}
    onDone={onDone}
  >
    <EntryDefaultsSection label="Condition">
      <SegmentedToggle<EntryCondition>
        className="segmented-toggle--calm"
        ariaLabel="Condition"
        options={CONDITION_OPTIONS}
        value={defaults.values.condition}
        onChange={(condition) => defaults.setValues({ condition })}
      />
    </EntryDefaultsSection>
    <EntryDefaultsSection label="Finish">
      <SegmentedToggle<CardFinish>
        className="segmented-toggle--calm"
        ariaLabel="Finish"
        options={FINISH_OPTIONS}
        value={defaults.values.finish}
        onChange={(finish) => defaults.setValues({ finish })}
      />
    </EntryDefaultsSection>
    <EntryDefaultsSection label="Language">
      <select
        className="entry-defaults-select"
        aria-label="Language"
        value={defaults.values.language}
        onChange={(e) => defaults.setValues({ language: e.target.value })}
      >
        {SUPPORTED_LANGUAGES.map((l) => (
          <option key={l.code} value={l.code}>
            {l.displayName}
          </option>
        ))}
      </select>
    </EntryDefaultsSection>
  </EntryDefaultsPill>
);

/** Want List: the accepted sets, which are the Settings version defaults. */
export const WantEntryDefaultsPill: React.FC<PillProps<WantEntryDefaults>> = ({ defaults, onDone }) => (
  <EntryDefaultsPill
    label="Accepting"
    summary={defaults.summary}
    isStock={defaults.isStock}
    title="New cards accept"
    footer="These are your Want List version defaults, same values as Settings."
    onReset={defaults.reset}
    onDone={onDone}
  >
    <EntryDefaultsSection label="Condition">
      <InlineConditionPills
        value={defaults.values.conditions}
        onChange={(conditions) => defaults.setValues({ conditions })}
      />
    </EntryDefaultsSection>
    <EntryDefaultsSection label="Finish">
      <InlineFoilPills
        value={defaults.values.finishes}
        onChange={(finishes) => defaults.setValues({ finishes })}
      />
    </EntryDefaultsSection>
    <EntryDefaultsSection label="Language">
      <WantLanguageChips
        value={defaults.values.languages}
        onChange={(languages) => defaults.setValues({ languages })}
      />
    </EntryDefaultsSection>
  </EntryDefaultsPill>
);
